/**
 * 試験セクションの表示ラベル（単一の正）
 *
 * これまで「午前/午前Ⅰ/午前Ⅱ/午後…」の文言が QuizPage / ExamsPage / MockPage 等に
 * 散在していた。IPA新制度（2026年度〜）で午前→科目A・午後→科目B へ呼称変更されるため、
 * 表示層の呼称を1箇所に集約し、併記の点灯をここだけの変更で行えるようにする。
 *
 * ⚠️ データ構造（ExamSection の値 morning/morning1/... や DB）は変更しない。あくまで表示層のラベルのみ。
 */

import { ExamSection } from "../types";

/** section コード → 現行呼称（午前Ⅰ/Ⅱ・午後Ⅰ/Ⅱ）。ローマ数字は全角(Ⅰ/Ⅱ)で統一 */
export const SECTION_LABEL: Record<ExamSection, string> = {
  morning: "午前",
  morning1: "午前Ⅰ",
  morning2: "午前Ⅱ",
  afternoon1: "午後Ⅰ",
  afternoon2: "午後Ⅱ",
};

/**
 * IPA新制度の科目呼称（午前→科目A / 午後→科目B、高度は科目A-1/A-2・B-1/B-2）。
 * 出典（一次情報で確定済み 2026-07-19）: IPA「CBT方式での実施について」
 * https://www.ipa.go.jp/shiken/2026/ap_koudo_sc-cbt.html
 * AP: 午前→科目A・午後→科目B / 高度・支援士: 午前Ⅰ→A-1・午前Ⅱ→A-2・午後Ⅰ→B-1・午後Ⅱ→B-2
 * 免除制度も同呼称で継続（午前Ⅰ免除→科目A-1試験免除）。
 */
export const SUBJECT_LABEL: Record<ExamSection, string> = {
  morning: "科目A",
  morning1: "科目A-1",
  morning2: "科目A-2",
  afternoon1: "科目B-1",
  afternoon2: "科目B-2",
};

/** section の現行呼称ラベルを返す（未知値は素通し） */
export function sectionLabel(section?: string | null): string {
  if (!section) return SECTION_LABEL.morning;
  return SECTION_LABEL[section as ExamSection] ?? SECTION_LABEL.morning;
}

/**
 * 現行呼称に新制度の科目呼称を併記したラベル。
 * withSubject=false（既定）では現行呼称のみ＝現状の表示を変えない。
 * 併記を点灯する際は呼び出し側で withSubject=true にする（例: 「午前Ⅱ（科目A-2）」）。
 */
export function sectionLabelWithSubject(
  section?: string | null,
  withSubject = false
): string {
  const base = sectionLabel(section);
  if (!withSubject || !section) return base;
  const subject = SUBJECT_LABEL[section as ExamSection];
  return subject ? `${base}（${subject}）` : base;
}
